import React, { forwardRef, useRef } from "react";
import styled, { css } from "styled-components";
import { ChevronDown, ChevronRight, Trash2 } from "react-feather";
import { Button } from "@cinesys/ui/components";

/**
 * The collapsible row shell for a Spotter field-list screen (Fields today; meant to be reused by
 * other field-list-shaped tabs too). A row is a clickable header (chevron, label, an optional
 * badge/summary, a Remove button) over a panel that expands/collapses. Mode-specific chrome (the
 * TypeTag badge, the panel's actual form controls) stays with each screen — this only owns the
 * shell, the expand/collapse animation, and the deferred-removal fade.
 *
 * ANIMATING TO/FROM `height: auto`: CSS can't transition to `auto` directly, so `handleToggle`
 * imperatively sets a concrete pixel height right as the row toggles (before the `expanded` prop
 * itself changes), giving the CSS `transition: height` something real to animate between; the
 * inline style is cleared once the transition ends, handing height back to `$expanded`'s own
 * `auto`/`0` rule for any subsequent content resize.
 */

const ROW_MAX_HEIGHT = "20000px";

const Row = styled.div<{
  $isNew?: boolean;
  $isEdited?: boolean;
  $removing?: boolean;
}>`
  background: var(--spotterSurface);
  border: 1px solid var(--spotterLine);
  border-radius: 11px;
  margin-bottom: 8px;
  overflow: hidden;
  box-shadow: var(--spotterShadow);
  max-height: ${ROW_MAX_HEIGHT};
  opacity: 1;
  transform: scaleY(1);
  transform-origin: top;
  transition: opacity 180ms ease, transform 180ms ease, max-height 220ms ease,
    margin-bottom 220ms ease;

  ${(p) =>
    p.$isNew &&
    css`
      border-color: var(--greenBorder);
    `}

  ${(p) =>
    p.$isEdited &&
    css`
      border-color: var(--spotterAccentLine);
    `}

  ${(p) =>
    p.$removing &&
    css`
      opacity: 0;
      transform: scaleY(0.97);
      max-height: 0;
      margin-bottom: 0;
      pointer-events: none;
    `}
`;

const Head = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 16px;
  align-items: center;
  padding: 8px 15px;
  cursor: pointer;
  background: var(--spotterSurface);
  transition: background-color 150ms ease;

  &:hover {
    background: var(--spotterRaised);
  }

  /* Below this width the name/badge/icon (all non-shrinking) and the Remove button were
     competing for the same ~half-width grid column and overlapping — give each its own row
     instead of squeezing them side by side. */
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
    row-gap: 6px;
  }
`;

const HeadLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;

  @media (max-width: 700px) {
    flex-wrap: wrap;
  }
`;

const RowLabel = styled.div`
  font-weight: 400;
  font-size: 15px;
  color: var(--gray400);
  flex: 0 0 auto;
`;

const RowSummary = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  color: var(--spotterInkFaint);
  margin-left: 6px;
`;

const HeadRight = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const PanelCollapse = styled.div<{ $expanded: boolean }>`
  overflow: hidden;
  height: ${(p) => (p.$expanded ? "auto" : "0")};
  transition: height 220ms ease;
`;

const Panel = styled.div`
  border-top: 1px solid var(--spotterLine);
  background: var(--spotterSurface);
  padding: 14px 15px;
`;

export interface CollapsibleFieldRowProps {
  isNew?: boolean;
  isEdited?: boolean;
  /** True while this row is mid exit-animation — the actual removal is deferred until
   *  `onRemoveTransitionEnd` fires (real DOM `max-height` `transitionend`, not a timer), so the
   *  fade/collapse has something to animate away instead of an instant unmount. */
  isRemoving?: boolean;
  onRemoveTransitionEnd?: () => void;
  expanded: boolean;
  onToggleExpand: () => void;
  expandTitle: string;
  collapseTitle: string;
  label: React.ReactNode;
  /** Rendered right after `label` — e.g. a mode-color `TypeTag`. Screen-specific, so it's a slot
   *  rather than something this shell knows how to render itself. */
  badge?: React.ReactNode;
  /** A trailing, truncating one-line hint — e.g. Fields' `summarizeRow`. */
  summary?: React.ReactNode;
  /** Rendered before the Remove button — e.g. a New/Edited pill. */
  headerRightExtra?: React.ReactNode;
  removeLabel?: string;
  onRemoveClick: () => void;
  children: React.ReactNode;
}

const CollapsibleFieldRow = forwardRef<
  HTMLDivElement,
  CollapsibleFieldRowProps
>(
  (
    {
      isNew,
      isEdited,
      isRemoving,
      onRemoveTransitionEnd,
      expanded,
      onToggleExpand,
      expandTitle,
      collapseTitle,
      label,
      badge,
      summary,
      headerRightExtra,
      removeLabel = "Remove",
      onRemoveClick,
      children,
    },
    ref
  ) => {
    const panelRef = useRef<HTMLDivElement | null>(null);

    const handleToggle = (): void => {
      const willExpand = !expanded;
      const el = panelRef.current;
      if (el) {
        if (willExpand) {
          el.style.height = `${el.scrollHeight}px`;
        } else {
          el.style.height = `${el.scrollHeight}px`;
          void el.offsetHeight;
          requestAnimationFrame(() => {
            el.style.height = "0px";
          });
        }
      }
      onToggleExpand();
    };

    return (
      <Row
        ref={ref}
        $isNew={isNew}
        $isEdited={isEdited}
        $removing={isRemoving}
        onTransitionEnd={(e) => {
          if (
            e.target === e.currentTarget &&
            e.propertyName === "max-height" &&
            isRemoving
          ) {
            onRemoveTransitionEnd?.();
          }
        }}
      >
        <Head onClick={handleToggle}>
          <HeadLeft>
            <Button
              borderless
              size={34}
              iconSize={21}
              icon={expanded ? ChevronDown : ChevronRight}
              onClick={(e) => {
                e.stopPropagation();
                handleToggle();
              }}
              title={expanded ? collapseTitle : expandTitle}
            />
            <RowLabel>{label}</RowLabel>
            {badge}
            {summary && <RowSummary>{summary}</RowSummary>}
          </HeadLeft>
          <HeadRight>
            {headerRightExtra}
            <Button
              label={removeLabel}
              icon={Trash2}
              type="danger"
              borderless
              onClick={(e) => {
                e.stopPropagation();
                onRemoveClick();
              }}
            />
          </HeadRight>
        </Head>
        <PanelCollapse
          $expanded={expanded}
          ref={panelRef}
          onTransitionEnd={(e) => {
            if (e.target === e.currentTarget && e.propertyName === "height") {
              (e.currentTarget as HTMLDivElement).style.height = "";
            }
          }}
        >
          <Panel>{children}</Panel>
        </PanelCollapse>
      </Row>
    );
  }
);
CollapsibleFieldRow.displayName = "CollapsibleFieldRow";

export default CollapsibleFieldRow;
